import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { ArrowLeft, Eye, EyeOff, ExternalLink, Loader2, LogOut, Lock, ShieldCheck } from "lucide-react";
import { Switch } from "@/components/ui/switch";
import { LogoMark } from "@/components/Logo";
import { api, formatApiError } from "@/lib/api";

const ICONS = { Boxes: null };

const AdminLogin = ({ onLogin }) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    try {
      const { data } = await api.post("/auth/login", { email, password });
      onLogin(data);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div data-testid="admin-login-modal" className="grid min-h-screen place-items-center bg-[#F8FAFC] px-6">
      <div className="w-full max-w-sm rounded-2xl border border-slate-200 bg-white p-8 shadow-[0_25px_60px_-25px_rgba(15,23,42,0.35)]">
        <div className="flex items-center gap-3">
          <LogoMark className="h-10 w-10" />
          <div>
            <h1 className="font-display text-lg font-extrabold text-[#0F172A]">Super Admin</h1>
            <p className="text-xs text-slate-500">Sign in to manage the portfolio</p>
          </div>
        </div>

        <form onSubmit={submit} className="mt-7 space-y-4">
          <div>
            <label htmlFor="admin-email" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
              Email
            </label>
            <input
              id="admin-email"
              data-testid="admin-login-email-input"
              type="email"
              className="w-full rounded-md border border-slate-200 px-4 py-3 text-sm outline-none transition-colors focus:border-[#F59E0B] focus:ring-2 focus:ring-[#F59E0B]/20"
              placeholder="admin@smrtin.net"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
            />
          </div>
          <div>
            <label htmlFor="admin-password" className="mb-1.5 block text-xs font-bold uppercase tracking-wide text-slate-500">
              Password
            </label>
            <div className="relative">
              <input
                id="admin-password"
                data-testid="admin-login-password-input"
                type={showPw ? "text" : "password"}
                className="w-full rounded-md border border-slate-200 px-4 py-3 pr-11 text-sm outline-none transition-colors focus:border-[#F59E0B] focus:ring-2 focus:ring-[#F59E0B]/20"
                placeholder="••••••••"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                required
              />
              <button
                type="button"
                data-testid="admin-login-password-toggle"
                onClick={() => setShowPw(!showPw)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                aria-label={showPw ? "Hide password" : "Show password"}
              >
                {showPw ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && (
            <p data-testid="admin-login-error" className="rounded-md bg-red-50 px-3 py-2.5 text-xs font-semibold text-red-600">
              {error}
            </p>
          )}

          <button
            type="submit"
            data-testid="admin-login-submit-btn"
            disabled={loading}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-[#0F172A] px-6 py-3 text-sm font-bold text-white transition-colors hover:bg-[#1e293b] disabled:opacity-60"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : <Lock className="h-4 w-4" />}
            Sign In
          </button>
        </form>

        <Link
          to="/"
          data-testid="admin-back-home-link"
          className="mt-6 inline-flex items-center gap-1.5 text-xs font-semibold text-slate-500 hover:text-[#0F172A]"
        >
          <ArrowLeft className="h-3.5 w-3.5" />
          Back to website
        </Link>
      </div>
    </div>
  );
};

const AdminPanel = ({ user, onLogout }) => {
  const [products, setProducts] = useState(null);
  const [leads, setLeads] = useState(null);

  useEffect(() => {
    Promise.all([api.get("/admin/products"), api.get("/admin/leads")])
      .then(([p, l]) => {
        setProducts(p.data);
        setLeads(l.data);
      })
      .catch((e) => {
        toast.error(formatApiError(e));
        setProducts([]);
        setLeads([]);
      });
  }, []);

  const toggle = async (product, next) => {
    const prev = products;
    setProducts(products.map((p) => (p.slug === product.slug ? { ...p, visible: next } : p)));
    try {
      await api.patch(`/admin/products/${product.slug}`, { visible: next });
      toast.success(`${product.title} is now ${next ? "visible" : "hidden"}.`);
    } catch (e) {
      setProducts(prev);
      toast.error(formatApiError(e));
    }
  };

  return (
    <div data-testid="admin-panel" className="min-h-screen bg-[#F8FAFC]">
      <header className="border-b border-slate-200 bg-white">
        <div className="mx-auto flex h-16 max-w-6xl items-center justify-between px-6">
          <div className="flex items-center gap-3">
            <LogoMark className="h-8 w-8" />
            <div>
              <p className="font-display text-sm font-extrabold text-[#0F172A]">Super Admin</p>
              <p className="text-[11px] text-slate-500">{user?.email}</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <Link
              to="/"
              data-testid="admin-view-site-link"
              className="inline-flex items-center gap-1.5 rounded-md border border-slate-200 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:text-[#0F172A]"
            >
              <ExternalLink className="h-3.5 w-3.5" />
              View Site
            </Link>
            <button
              data-testid="admin-logout-btn"
              onClick={onLogout}
              className="inline-flex items-center gap-1.5 rounded-md bg-[#0F172A] px-3.5 py-2 text-xs font-semibold text-white hover:bg-[#1e293b]"
            >
              <LogOut className="h-3.5 w-3.5" />
              Logout
            </button>
          </div>
        </div>
      </header>

      <main className="mx-auto max-w-6xl space-y-8 px-6 py-10">
        <section data-testid="admin-products-section">
          <h2 className="font-display text-xl font-extrabold text-[#0F172A]">Product Visibility</h2>
          <p className="mt-1 text-sm text-slate-500">
            Hidden products disappear from the public website immediately.
          </p>

          <div className="mt-5 divide-y divide-slate-100 rounded-2xl border border-slate-200 bg-white">
            {products === null && (
              <div className="grid place-items-center py-14 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {products?.map((p) => (
              <div key={p.slug} className="flex items-center justify-between gap-4 px-6 py-4">
                <div>
                  <p className="text-sm font-bold text-[#0F172A]">{p.title}</p>
                  <a
                    href={p.link}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-1 text-xs text-slate-500 hover:text-[#0F172A]"
                  >
                    {p.link}
                    <ExternalLink className="h-3 w-3" />
                  </a>
                </div>
                <div className="flex items-center gap-3">
                  <span
                    data-testid={`admin-product-status-${p.slug}`}
                    className={`font-mono text-[10px] font-semibold uppercase tracking-[0.15em] ${
                      p.visible ? "text-emerald-600" : "text-slate-400"
                    }`}
                  >
                    {p.visible ? "Visible" : "Hidden"}
                  </span>
                  <Switch
                    checked={p.visible}
                    onCheckedChange={(next) => toggle(p, next)}
                    data-testid={`admin-toggle-product-${p.slug}`}
                    aria-label={`Toggle ${p.title} visibility`}
                  />
                </div>
              </div>
            ))}
          </div>
        </section>

        <section data-testid="admin-leads-section">
          <div className="flex items-center justify-between">
            <h2 className="font-display text-xl font-extrabold text-[#0F172A]">Leads</h2>
            {leads && (
              <span
                data-testid="admin-leads-count"
                className="rounded-full bg-amber-100 px-3 py-1 font-mono text-[11px] font-semibold text-amber-700"
              >
                {leads.length}
              </span>
            )}
          </div>

          <div className="mt-5 overflow-hidden rounded-2xl border border-slate-200 bg-white">
            {leads === null && (
              <div className="grid place-items-center py-14 text-slate-400">
                <Loader2 className="h-5 w-5 animate-spin" />
              </div>
            )}
            {leads?.length === 0 && (
              <p className="px-6 py-10 text-center text-sm text-slate-500">
                No enquiries yet — submissions from the contact form will appear here.
              </p>
            )}
            {leads?.length > 0 && (
              <div className="overflow-x-auto">
                <table className="w-full min-w-[720px] text-left text-sm">
                  <thead>
                    <tr className="border-b border-slate-100 bg-slate-50/60 font-mono text-[10px] uppercase tracking-[0.15em] text-slate-500">
                      <th className="px-6 py-3">Name</th>
                      <th className="px-6 py-3">Company</th>
                      <th className="px-6 py-3">Email</th>
                      <th className="px-6 py-3">Phone</th>
                      <th className="px-6 py-3">Product</th>
                      <th className="px-6 py-3">Date</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {leads.map((l, i) => (
                      <tr key={i} className="text-slate-700">
                        <td className="px-6 py-3.5 font-semibold text-[#0F172A]">{l.name}</td>
                        <td className="px-6 py-3.5">{l.company}</td>
                        <td className="px-6 py-3.5">{l.email}</td>
                        <td className="px-6 py-3.5">{l.phone}</td>
                        <td className="px-6 py-3.5">{l.product}</td>
                        <td className="px-6 py-3.5 text-xs text-slate-500">
                          {new Date(l.created_at).toLocaleDateString()}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      </main>
    </div>
  );
};

export default function Admin() {
  const [user, setUser] = useState(undefined); // undefined = checking

  useEffect(() => {
    const check = async () => {
      try {
        const { data } = await api.get("/auth/me");
        setUser(data);
        return;
      } catch {
        /* try refresh below */
      }
      try {
        await api.post("/auth/refresh");
        const { data } = await api.get("/auth/me");
        setUser(data);
      } catch {
        setUser(null);
      }
    };
    check();
  }, []);

  if (user === undefined) {
    return (
      <div className="grid min-h-screen place-items-center bg-[#F8FAFC] text-slate-400">
        <Loader2 className="h-6 w-6 animate-spin" />
      </div>
    );
  }

  if (!user) {
    return <AdminLogin onLogin={(u) => setUser(u)} />;
  }

  return (
    <AdminPanel
      user={user}
      onLogout={async () => {
        try {
          await api.post("/auth/logout");
        } finally {
          setUser(null);
        }
      }}
    />
  );
}
